// Bookkeeping for the app's overlay dialogs, kept free of the DOM so it can be
// unit tested. useDialog() is the only caller.
//
// Overlays nest — Edit Itinerary opens Add Sites, which opens the filter
// flyout; Edit also opens the delete and cancel confirmations — so Escape and
// the Tab trap must act on the TOPMOST one only. Each overlay used to bind its
// own document keydown listener, and the outer one, registered first, ran
// first: a single Escape closed Edit out from under its own confirmation.

export type DialogEntry = {
    id: number;
    onEscape?: () => void;
};

export function createDialogStack<T extends DialogEntry>() {
    const entries: T[] = [];

    return {
        push(entry: T) {
            entries.push(entry);
        },
        remove(entry: T) {
            const index = entries.indexOf(entry);
            if (index !== -1) entries.splice(index, 1);
        },
        top(): T | undefined {
            return entries[entries.length - 1];
        },
        get size() {
            return entries.length;
        },
    };
}

// Where Tab should send focus to keep it inside a dialog, or null to let the
// browser move it normally. `focusables` is the dialog's tabbable elements in
// DOM order; `active` is document.activeElement.
export function trapTarget<E>(focusables: E[], active: E | null, shift: boolean): E | null {
    if (focusables.length === 0) return null;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const index = active === null ? -1 : focusables.indexOf(active);

    // Focus is on the dialog container itself, or has escaped it entirely.
    if (index === -1) return shift ? last : first;

    if (shift && index === 0) return last;
    if (!shift && index === focusables.length - 1) return first;
    return null;
}
